import { Stack } from "@chakra-ui/react";
import { useEffect } from "react";

import { useGetAccountDetails } from "../../api/useGetAccountDetails";
import useGetCallOrAlert, { CallAlertData } from "../../api/useGetCallOrAlert";
import useGetCustomerSummary, {
  CustomerSummaryData,
} from "../../api/useGetCustomerSummary";
import { MarginsResponse, useGetMargins } from "../../api/useGetMargins";
import { EventType } from "../../types/marginCallsAndAlerts";
import { getApiFormattedDate } from "../../utils/dateFormatter";
import { Currency, formatCurrency } from "../../utils/formatCurrency";
import getDateString from "../../utils/getDateString";
import InlineError from "../InlineError";
import { Data, getCurrencyValue, getDataSection } from "./auxUtils";

interface SummaryProps {
  callId: string;
  accountId: string;
  processDate: string;
}

function Summary({ callId, accountId, processDate }: SummaryProps) {
  const {
    data: callAlertData,
    isLoading: isCallAlertLoading,
    error: callAlertError,
  } = useGetCallOrAlert(callId);

  const {
    data: marginAccountData,
    isLoading: isMarginAccountLoading,
    error: marginAccountError,
  } = useGetMargins(accountId);

  const {
    data: customerSummaryData,
    isLoading: isCustomerSummaryLoading,
    error: customerSummaryError,
  } = useGetCustomerSummary(accountId, getApiFormattedDate(processDate));

  const {
    data: accountDetails,
    refetch: getAccountDetails,
    error: accountDetailsError,
  } = useGetAccountDetails(accountId);

  useEffect(() => {
    getAccountDetails();
  }, [getAccountDetails]);

  const buildCallSummary = (callAlertData: CallAlertData): Data[] => {
    const {
      eventType,
      eventName,
      description,
      callAmount,
      eventCode,
      dueDate,
      issueDate,
      closedDate,
      tradeDate,
      status,
    } = callAlertData;
    return [
      { label: "Type", value: eventType },
      { label: "Status", value: status },
      {
        label: "Event Name",
        value: eventType === EventType.Call ? eventName : description,
      },
      { label: "Call Type", value: eventCode },
      {
        label: "Amount",
        value: callAmount
          ? formatCurrency(Currency.Usd, String(callAmount))
          : "",
      },
      { label: "Due Date", value: getDateString(dueDate) },
      { label: "Issue Date", value: getDateString(issueDate) },
      { label: "Trade Date", value: getDateString(tradeDate) },
      ...(closedDate
        ? [{ label: "Closed Date", value: getDateString(closedDate) }]
        : []),
    ];
  };

  const buildAccountSummary = (marginAccountData: MarginsResponse): Data[] => {
    const {
      dayTradeBuyingPower,
      netLiquidity,
      cashExcessEquity,
      marginExcess,
      totalConcentrationExcess,
      marginEquity,
      marginConcentrationExcess,
    } = marginAccountData;
    return [
      {
        label: "Intraday Buying Power", // Updated label (FEECM-17670)
        value: getCurrencyValue(dayTradeBuyingPower),
        isNumeric: true,
      },
      {
        label: "Net Liquidating Value",
        value: getCurrencyValue(netLiquidity),
        isNumeric: true,
      },
      {
        label: "Total Excess",
        value: getCurrencyValue(cashExcessEquity),
        isNumeric: true,
      },
      {
        label: "Total Concentration Excess",
        value: getCurrencyValue(totalConcentrationExcess),
        isNumeric: true,
      },
      {
        label: "Margin Equity",
        value: getCurrencyValue(marginEquity),
        isNumeric: true,
      },
      {
        label: "Margin Excess Equity",
        value: getCurrencyValue(marginExcess),
        isNumeric: true,
      },
      {
        label: "Margin Concentration Excess",
        value: getCurrencyValue(marginConcentrationExcess),
        isNumeric: true,
      },
    ];
  };

  const buildCustomerSummary = (customerSummaryData: CustomerSummaryData) => {
    const {
      accountName,
      correspondentCode,
      officeRepCode,
      closeRestriction,
      restrictReason,
      restrictDate,
      account,
      dayTradeCount,
      dayTraderType,
    } = customerSummaryData;

    let accountRegistrationValue, marginTypeValue;
    if (accountDetails) {
      const { accountRegistration, marginType } = accountDetails;
      accountRegistrationValue = accountRegistration;
      marginTypeValue = marginType;
    } else {
      accountRegistrationValue = "error";
      marginTypeValue = "error";
    }

    return [
      {
        label: "Account Name",
        value: accountName,
      },
      {
        label: "Correspondent",
        value: correspondentCode,
      },
      {
        label: "Office/Rep. Code",
        value: officeRepCode,
      },
      {
        label: "Close Restriction",
        value: closeRestriction,
      },
      {
        label: "Restrict Reason",
        value: restrictReason,
      },
      {
        label: "Restrict Date",
        value: restrictDate ? getDateString(restrictDate) : <>&mdash;</>,
      },
      {
        label: "Account ID",
        value: account,
      },
      {
        label: "Account Registration",
        value: accountDetailsError ? <InlineError /> : accountRegistrationValue,
      },
      {
        label: "Margin Type",
        value: accountDetailsError ? <InlineError /> : marginTypeValue,
      },
      {
        label: "Day Trade Count",
        value: dayTradeCount,
      },
      {
        label: "Day Trader Status",
        value: dayTraderType ? dayTraderType : "non-PDT",
      },
    ];
  };

  return (
    <Stack spacing={0}>
      {getDataSection({
        title: "Call",
        data: callAlertData ? buildCallSummary(callAlertData) : [],
        isLoading: isCallAlertLoading,
        error: callAlertError,
        skeletonLines: 7,
      })}
      {getDataSection({
        title: "Account",
        data: marginAccountData ? buildAccountSummary(marginAccountData) : [],
        isLoading: isMarginAccountLoading,
        error: marginAccountError,
        skeletonLines: 7,
      })}
      {getDataSection({
        title: "Customer",
        data: customerSummaryData
          ? buildCustomerSummary(customerSummaryData)
          : [],

        isLoading: isCustomerSummaryLoading,
        error: customerSummaryError,
        skeletonLines: 11,
      })}
    </Stack>
  );
}

export default Summary;
